import { useForm } from 'react-hook-form'
import { Upload, X, Image } from 'lucide-react'
import { useEffect } from 'react'
import { Button } from '../common'
import { getFullLogoUrl } from '../../utils/urlUtils'

function CompanyForm({ 
  isOpen, 
  editingCompany, 
  onClose, 
  onSubmit, 
  isLoading,
  selectedLogo,
  logoPreview,
  onLogoSelect,
  onLogoUpload,
  onLogoDelete,
  onRemoveLogoPreview,
  isUploadingLogo
}) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  // Reset form with editing company data when editingCompany changes
  useEffect(() => {
    if (editingCompany) {
      reset({
        name: editingCompany.name || '',
        email: editingCompany.email || '',
        phone: editingCompany.phone || '',
        website: editingCompany.website || '',
        address: editingCompany.address || '',
        city: editingCompany.city || '',
        state: editingCompany.state || '',
        pincode: editingCompany.pincode || '',
        country: editingCompany.country || '',
        gstNumber: editingCompany.gstNumber || '',
        panNumber: editingCompany.panNumber || ''
      })
    } else {
      reset({
        name: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        country: '',
        gstNumber: '',
        panNumber: ''
      })
    }
  }, [editingCompany, reset])

  const handleSubmitForm = (data) => {
    onSubmit(data)
  }

  const handleClose = () => {
    onClose()
    // Reset form to empty values when closing
    reset({
      name: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      country: '',
      gstNumber: '',
      panNumber: ''
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="modal-overlay" onClick={handleClose} />
        <div className="modal-content max-w-2xl">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">
            {editingCompany ? 'Edit Company' : 'Add New Company'}
          </h3>
          <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Company Name</label>
                <input
                  {...register('name', { required: 'Company name is required' })}
                  className="input"
                  placeholder="Enter company name"
                />
                {errors.name && (
                  <p className="form-error">{errors.name.message}</p>
                )}
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  {...register('email', {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  className="input"
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <p className="form-error">{errors.email.message}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Phone</label>
                <input
                  {...register('phone')}
                  className="input"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="label">Website</label>
                <input
                  {...register('website')}
                  className="input"
                  placeholder="Enter website URL"
                />
              </div>
            </div>
            <div>
              <label className="label">Address</label>
              <textarea
                {...register('address')}
                className="input"
                rows={2}
                placeholder="Enter company address"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">City</label>
                <input
                  {...register('city')}
                  className="input"
                  placeholder="Enter city"
                />
              </div>
              <div>
                <label className="label">State</label>
                <input
                  {...register('state')}
                  className="input"
                  placeholder="Enter state"
                />
              </div>
              <div>
                <label className="label">Pincode</label>
                <input
                  {...register('pincode')}
                  className="input"
                  placeholder="Enter pincode"
                />
              </div>
              <div>
                <label className="label">Country</label>
                <input
                  {...register('country')}
                  className="input"
                  placeholder="Enter country"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">GST Number</label>
                <input
                  {...register('gstNumber')}
                  className="input"
                  placeholder="Enter GST number"
                />
              </div>
              <div>
                <label className="label">PAN Number</label>
                <input
                  {...register('panNumber')}
                  className="input"
                  placeholder="Enter PAN number"
                />
              </div>
            </div>
            
            {/* Logo Upload Section */}
            <div>
              <label className="label">Company Logo</label>
              <div className="flex items-center space-x-4">
                {logoPreview ? (
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="h-16 w-16 object-cover rounded-lg border border-secondary-200"
                    />
                    <button
                      type="button"
                      onClick={onRemoveLogoPreview}
                      className="absolute -top-2 -right-2 bg-danger-500 text-white rounded-full p-1 hover:bg-danger-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : editingCompany?.logo ? (
                  <div className="relative">
                    <img
                      src={getFullLogoUrl(editingCompany.logo)}
                      alt="Current logo"
                      className="h-16 w-16 object-cover rounded-lg border border-secondary-200"
                    />
                    <button
                      type="button"
                      onClick={() => onLogoDelete(editingCompany.id)}
                      className="absolute -top-2 -right-2 bg-danger-500 text-white rounded-full p-1 hover:bg-danger-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="h-16 w-16 bg-secondary-100 rounded-lg border border-secondary-200 flex items-center justify-center">
                    <Image className="h-6 w-6 text-secondary-400" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onLogoSelect}
                    className="hidden"
                    id="logo-upload-modal"
                  />
                  <label
                    htmlFor="logo-upload-modal"
                    className="btn-outline cursor-pointer inline-flex items-center"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {logoPreview || editingCompany?.logo ? 'Change Logo' : 'Upload Logo'}
                  </label>
                  {selectedLogo && (
                      <button
                        type="button"
                        onClick={() => onLogoUpload(editingCompany?.id)}
                        className="btn-primary ml-2"
                        disabled={isUploadingLogo}
                      >
                        {isUploadingLogo ? 'Uploading...' : 'Upload'}
                      </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={handleClose}
                variant="outline"
                size="md"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                variant="primary"
                size="md"
                loading={isLoading}
              >
                {editingCompany ? 'Update Company' : 'Create Company'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CompanyForm
